// Author: Ahsan Saleem
import { useCallback, useEffect, useRef, useState } from 'react';
import { outboxItems } from './outbox.js';
import { getSyncState } from './db.js';

/** Minimal async-data hook with manual refresh; server state is not component state. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): {
  data: T | undefined; error: string | undefined; loading: boolean; reload: () => void;
} {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setLoading(true);
    fn().then(
      (d) => { if (live) { setData(d); setError(undefined); } },
      (e) => { if (live) setError(e instanceof Error ? e.message : String(e)); },
    ).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);
  return { data, error, loading, reload: useCallback(() => setTick((t) => t + 1), []) };
}

/**
 * Live data — like useAsync but self-refreshing on an interval WITHOUT flashing the
 * loading state: the surface keeps its last value and swaps in the new one when it
 * arrives (a background poll, paused while the tab is hidden or offline). This is
 * what makes a surface feel operated rather than fetched. `refreshedAt` (epoch ms)
 * drives freshness indicators; a transient background failure keeps the last good data.
 */
export function useLive<T>(fn: () => Promise<T>, deps: unknown[] = [], intervalMs = 12000): {
  data: T | undefined; error: string | undefined; loading: boolean; refreshedAt: number | undefined; reload: () => void;
} {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<number>();
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let live = true;
    const load = (initial: boolean) => {
      if (initial) setLoading(true);
      fnRef.current().then(
        (d) => { if (live) { setData(d); setError(undefined); setRefreshedAt(Date.now()); } },
        (e) => { if (live && initial) setError(e instanceof Error ? e.message : String(e)); },
      ).finally(() => { if (live && initial) setLoading(false); });
    };
    load(true);
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) load(false);
    }, intervalMs);
    return () => { live = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, intervalMs, ...deps]);
  return { data, error, loading, refreshedAt, reload: useCallback(() => setTick((t) => t + 1), []) };
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

export function useSync(): { pending: number; failed: number; lastSuccessAt?: string } {
  const [state, setState] = useState<{ pending: number; failed: number; lastSuccessAt?: string }>({ pending: 0, failed: 0 });
  const refresh = useCallback(async () => {
    const items = await outboxItems();
    const sync = await getSyncState();
    setState({
      pending: items.filter((i) => i.state !== 'failed').length,
      failed: items.filter((i) => i.state === 'failed').length,
      lastSuccessAt: sync.lastSuccessAt,
    });
  }, []);
  useEffect(() => {
    void refresh();
    window.addEventListener('bloomos-sync', refresh);
    return () => window.removeEventListener('bloomos-sync', refresh);
  }, [refresh]);
  return state;
}
